import Card from "@/components/Card";
import React, { useEffect } from "react";
import { columns } from "./columns";
import { AggregatePayslip } from "@/models/payslips.model";
import {
  generatePayslipTablePdf,
  getPayslipTableHtml,
  getProcessedPayslipsAggregate,
} from "@/api/payslips";
import { months } from "@/utils/date-format";
import DataTable from "@/components/ui/DataTable";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { PayRecordsToolbar } from "./PayRecordsToolbar";
import { Button } from "@/components/ui/button";
import { DownloadIcon, TableProperties } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import Searchbar from "@/components/search/Searchbar";

interface Props {
  selectedYear: string;
  selectedMonth: string;
}
function PayRecordsDataTable({ selectedYear, selectedMonth }: Props) {
  const [data, setData] = React.useState<AggregatePayslip[]>([]);
  useEffect(() => {
    const getData = async () => {
      try {
        const data = await getProcessedPayslipsAggregate(
          Number(selectedYear),
          months.indexOf(selectedMonth) + 1
        );
        console.log(data);
        setData(data);
      } catch (err) {
        console.error(err);
        toast({
          variant: "destructive",
          title: "Failed to fetch records",
          description: "Please try again later",
        });
      }
    };
    getData();
  }, [selectedMonth, selectedYear]);

  const getTableBlb = async () => {
    try {
      const response = await generatePayslipTablePdf(
        months.indexOf(selectedMonth) + 1,
        Number(selectedYear)
      );
      // If your API returns a blob URL
      if (typeof response === 'string' && response.startsWith('blob:')) {
        // Fetch the actual blob data
        const blobResponse = await fetch(response);
        if (!blobResponse.ok) {
          throw new Error('Failed to fetch blob data');
        }
        const blob = await blobResponse.blob();
        return { blob, blobUrl: response };
      }
      // If your API directly returns a blob-like object
      else if (
        response &&
        typeof response === 'object' &&
        typeof (response as any).size === 'number' &&
        typeof (response as any).type === 'string'
      ) {
        const blobUrl = URL.createObjectURL(response as Blob);
        return { blob: response as Blob, blobUrl };
      }
      // Handle unexpected response type
      else {
        throw new Error('Unexpected response format from generate table');
      }
    } catch (error) {
      console.error("Error generating table pdf", error);
      toast({
        title: "Error generating table pdf",
        variant: "destructive",
        description: "Please try again later",
      });
      throw error;
    }
  };

  const viewPdf = async () => {
    try {
      const { blobUrl } = await getTableBlb();
      window.open(blobUrl, "_blank");
      // Don't revoke the URL immediately since the user may want to download
      // Keep it for a longer time
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000); // 1 minute
    } catch (error) {
      // Error already handled in getPayslipBlob
    }
  };

  if (!selectedMonth || !selectedYear) {
    return <Skeleton className="h-full w-full " />;
  }
  return (
    <Card styles="h-full flex w-full">
      <div className="rounded-md w-full h-full p-5 bg-white outline outline-2 outline-gray-100 flex flex-col gap-3">
        <div className="flex  justify-between items-start">
          <div className="flex flex-col gap-3">
            <div className="font-bold mt-2 text-xl">Processed List</div>
            <div className="text-sm text-gray-500">
              View processed payslips for the period: {selectedMonth}{" "}
              {selectedYear}
            </div>
          </div>
          <Button onClick={viewPdf}>
            <TableProperties className="w-6 h-6" />
            Table View
          </Button>
        </div>


        <div className="flex-grow overflow-y-auto">
          <DataTable
            tableMinWidth="400px"
            data={data}
            columns={columns}
            tableCaption={`A total of ${data.length} employees with processed payslips.`}
          />
        </div>
      </div>
    </Card>
  );
}

export default PayRecordsDataTable;
